import React from 'react';
import { motion } from 'framer-motion';
import { 
  Factory, 
  Shield, 
  Lock, 
  Server, 
  Network,
  Database,
  Cog,
  Cpu,
  CheckCircle,
  ArrowRight,
  Cloud,
  AlertTriangle
} from 'lucide-react';

interface ManufacturingProps {
  setCurrentPage: (page: string) => void;
}

const Manufacturing: React.FC<ManufacturingProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "OT/IT Security Integration",
      description: "Comprehensive security solutions bridging operational technology and IT infrastructure.",
      items: [
        "Network segmentation",
        "Security monitoring",
        "Access control",
        "Threat detection"
      ]
    },
    {
      icon: Lock,
      title: "Industrial Control Systems",
      description: "Advanced protection for critical industrial control systems and SCADA networks.",
      items: [
        "ICS security",
        "SCADA protection",
        "PLC security",
        "Real-time monitoring"
      ]
    },
    {
      icon: Network,
      title: "Smart Factory Security",
      description: "Specialized security solutions for Industry 4.0 and smart manufacturing.",
      items: [
        "IoT device security",
        "Automation security",
        "Data protection",
        "Network security"
      ]
    },
    {
      icon: Server,
      title: "Supply Chain Protection",
      description: "End-to-end security for manufacturing supply chain and vendor networks.",
      items: [
        "Vendor access control",
        "Data encryption",
        "Third-party security",
        "Risk management"
      ]
    }
  ];

  const solutions = [
    {
      icon: Database,
      title: "Data Protection",
      items: [
        "IP protection",
        "Trade secret security",
        "Data encryption",
        "Access controls"
      ]
    },
    {
      icon: Cloud,
      title: "Cloud Manufacturing",
      items: [
        "Cloud security",
        "Remote access",
        "Data storage",
        "Application security"
      ]
    },
    {
      icon: AlertTriangle,
      title: "Risk Management",
      items: [
        "Risk assessment",
        "Compliance monitoring",
        "Incident response",
        "Business continuity"
      ]
    }
  ];

  const capabilities = [
    {
      icon: Cog,
      title: "Process Security",
      description: "Secure manufacturing processes and operations with advanced protection measures.",
      features: [
        "Process monitoring",
        "Quality control security",
        "Operational security",
        "Workflow protection"
      ]
    },
    {
      icon: Cpu,
      title: "IIoT Security",
      description: "Comprehensive security for Industrial Internet of Things devices and networks.",
      features: [
        "Device authentication",
        "Network protection",
        "Data security",
        "Threat detection"
      ]
    },
    {
      icon: Shield,
      title: "Cyber Defense",
      description: "Advanced cyber defense solutions for manufacturing environments.",
      features: [
        "Threat intelligence",
        "Attack prevention",
        "Security monitoring",
        "Incident response"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Factory className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Manufacturing Security Solutions</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Advanced cybersecurity solutions designed specifically for manufacturing environments, protecting your operational technology and industrial systems
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Solutions Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Security Solutions</h2>
            <p className="text-lg text-muted">
              Comprehensive security solutions for modern manufacturing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <solution.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{solution.title}</h3>
                </div>
                
                <ul className="space-y-3">
                  {solution.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Capabilities Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Advanced Capabilities</h2>
            <p className="text-lg text-muted">
              State-of-the-art security capabilities for manufacturing environments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <capability.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{capability.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{capability.description}</p>

                <ul className="space-y-3">
                  {capability.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Secure Your Manufacturing Operations</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our manufacturing security experts to discuss your facility's specific needs and discover how we can help protect your operations
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Manufacturing;